import { defineMessages, useIntl } from "react-intl";

import Link from "common/core/link";
import { redirectUrl } from "util/application_redirect";
import { useProofRebrand } from "util/feature_detection";
import type { ProofPortal } from "constants/app_subdomains";
import { isIndependentNotary } from "common/notary/capacity";
import { GET_STARTED_PATH } from "common/proof_frame/path";
import { TRANSACTION_PATH } from "util/routes";
import NotarizeSignerPortalWhite from "assets/images/logos/notarize-logo-full-white.svg";
import NotarizeSignerPortalColor from "assets/images/logos/notarize-logo-full.svg";
import NotarizeLenderPortalColor from "assets/images/logos/portal-logos/notarize-lender.svg";
import NotarizeNotaryPortalColor from "assets/images/logos/portal-logos/notarize-notary.svg";
import NotarizeBusinessPortalColor from "assets/images/logos/portal-logos/notarize-business.svg";
import NotarizeTitlePortalWhite from "assets/images/logos/portal-logos/notarize-title-white.svg";
import NotarizeLenderPortalWhite from "assets/images/logos/portal-logos/notarize-lender-white.svg";
import NotarizeNotaryPortalWhite from "assets/images/logos/portal-logos/notarize-notary-white.svg";
import NotarizeBusinessPortalWhite from "assets/images/logos/portal-logos/notarize-business-white.svg";
import NotarizeTitlePortalColor from "assets/images/logos/portal-logos/notarize-title.svg";
import ProofSignerPortalWhite from "assets/images/logos/proof-logo-full-white.svg";
import ProofSignerPortalDark from "assets/images/logos/proof-logo-full.svg";
import ProofLenderPortalDark from "assets/images/logos/portal-logos/proof-lender-dark.svg";
import ProofNotaryPortalDark from "assets/images/logos/portal-logos/proof-notary-dark.svg";
import ProofBusinessPortalDark from "assets/images/logos/portal-logos/proof-business-dark.svg";
import ProofTitlePortalWhite from "assets/images/logos/portal-logos/proof-title-light.svg";
import ProofLenderPortalWhite from "assets/images/logos/portal-logos/proof-lender-light.svg";
import ProofNotaryPortalWhite from "assets/images/logos/portal-logos/proof-notary-light.svg";
import ProofBusinessPortalWhite from "assets/images/logos/portal-logos/proof-business-light.svg";
import ProofTitlePortalDark from "assets/images/logos/portal-logos/proof-title-dark.svg";

import type { GlobalNavUser } from "../index.fragment.graphql";
import Styles from "./index.module.scss";

const MESSAGES = defineMessages({
  logoUrlLabel: {
    id: "158f7db1-75e2-4371-8871-5be90c04a59e",
    defaultMessage: "Return to home",
  },
  notarizeSignerLogoAlt: {
    id: "f55a6475-c4aa-4219-8e56-6c2a794cf4f2",
    defaultMessage: "Notarize for signers",
  },
  notarizeBusinessLogoAlt: {
    id: "64240c69-e9c6-4349-bd2d-23846f27dad6",
    defaultMessage: "Notarize for business",
  },
  notarizeLenderLogoAlt: {
    id: "c1a5f73e-b7e7-4bda-bb4d-d75cbb389f34",
    defaultMessage: "Notarize for lenders",
  },
  notarizeTitleLogoAlt: {
    id: "62545332-5a73-41ca-80e2-ab6ceb8ef2a3",
    defaultMessage: "Notarize for title",
  },
  notarizeNotaryLogoAlt: {
    id: "df70bd6f-a8f1-4275-ae4d-a75b08b56c8b",
    defaultMessage: "Notarize for notaries",
  },
  signerLogoAlt: {
    id: "4bedcf31-686a-4c20-8af0-1840bea63cc7",
    defaultMessage: "Proof for signers",
  },
  businessLogoAlt: {
    id: "73ef66c2-7cf9-49a3-8f21-9f70a56ecbd7",
    defaultMessage: "Proof for business",
  },
  lenderLogoAlt: {
    id: "cbdf0e30-7499-4394-8e43-e68747f3a27c",
    defaultMessage: "Proof for lenders",
  },
  titleLogoAlt: {
    id: "83e394ef-692d-4e68-8df5-07f59de8f97a",
    defaultMessage: "Proof for title",
  },
  notaryLogoAlt: {
    id: "43537f92-456c-4a1f-a54c-062a4c9e928d",
    defaultMessage: "Proof for notaries",
  },
});

type GlobalNavProps = {
  whiteLogo?: boolean;
  portal: ProofPortal;
  currentPortal: ProofPortal;
  user: GlobalNavUser;
};

// The Notary portal is not a real Proof Portal. It is just a re-skinned business portal.
// NST and ODN live in the business portal but we want it to be branded as the Notary App
// Otherwise all links/interactions will act according to "business portal" rules. If this last bit changes,
// we'll need to hoist some of this logic up and make "notary" a real portal.
function getEffectivePortal(inputPortal: ProofPortal, user: GlobalNavUser): ProofPortal | "notary" {
  if (inputPortal === "business" && isIndependentNotary(user.notaryProfile)) {
    return "notary";
  }
  return inputPortal;
}

function getDefaultPortalPath(user: GlobalNavUser) {
  if (isIndependentNotary(user.notaryProfile)) {
    return GET_STARTED_PATH;
  }
  return TRANSACTION_PATH;
}

export function PortalLogo({ portal, currentPortal, whiteLogo = true, user }: GlobalNavProps) {
  const intl = useIntl();
  const isProof = useProofRebrand();
  const isCurrentPortal = portal === currentPortal;
  const url = isCurrentPortal ? "/" : redirectUrl(portal) + getDefaultPortalPath(user);
  const linkProps = isCurrentPortal ? { to: url } : { href: url };

  function getNotarizePortalLogo(portal: ProofPortal, user: GlobalNavUser) {
    const effectivePortal = getEffectivePortal(portal, user);

    const logoAttributes = {
      app: {
        src: whiteLogo ? NotarizeSignerPortalWhite : NotarizeSignerPortalColor,
        alt: intl.formatMessage(MESSAGES.notarizeSignerLogoAlt),
      },
      business: {
        src: whiteLogo ? NotarizeBusinessPortalWhite : NotarizeBusinessPortalColor,
        alt: intl.formatMessage(MESSAGES.notarizeBusinessLogoAlt),
      },
      lender: {
        src: whiteLogo ? NotarizeLenderPortalWhite : NotarizeLenderPortalColor,
        alt: intl.formatMessage(MESSAGES.notarizeLenderLogoAlt),
      },
      title: {
        src: whiteLogo ? NotarizeTitlePortalWhite : NotarizeTitlePortalColor,
        alt: intl.formatMessage(MESSAGES.notarizeTitleLogoAlt),
      },
      notary: {
        src: whiteLogo ? NotarizeNotaryPortalWhite : NotarizeNotaryPortalColor,
        alt: intl.formatMessage(MESSAGES.notarizeNotaryLogoAlt),
      },
    };

    return logoAttributes[effectivePortal];
  }

  function getPortalLogo(portal: ProofPortal, user: GlobalNavUser) {
    const effectivePortal = getEffectivePortal(portal, user);

    const logoAttributes = {
      app: {
        src: whiteLogo ? ProofSignerPortalWhite : ProofSignerPortalDark,
        alt: intl.formatMessage(MESSAGES.signerLogoAlt),
      },
      business: {
        src: whiteLogo ? ProofBusinessPortalWhite : ProofBusinessPortalDark,
        alt: intl.formatMessage(MESSAGES.businessLogoAlt),
      },
      lender: {
        src: whiteLogo ? ProofLenderPortalWhite : ProofLenderPortalDark,
        alt: intl.formatMessage(MESSAGES.lenderLogoAlt),
      },
      title: {
        src: whiteLogo ? ProofTitlePortalWhite : ProofTitlePortalDark,
        alt: intl.formatMessage(MESSAGES.titleLogoAlt),
      },
      notary: {
        src: whiteLogo ? ProofNotaryPortalWhite : ProofNotaryPortalDark,
        alt: intl.formatMessage(MESSAGES.notaryLogoAlt),
      },
    };

    return logoAttributes[effectivePortal];
  }
  const { src, alt } = isProof ? getPortalLogo(portal, user) : getNotarizePortalLogo(portal, user);

  return (
    <div className={Styles.portalLogo}>
      <Link aria-label={intl.formatMessage(MESSAGES.logoUrlLabel)} {...linkProps} openInCurrentTab>
        <img src={src} alt={alt} />
      </Link>
    </div>
  );
}
